import { useNavigate } from 'react-router'
import { Async } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { HeroTile, Panel } from '../../platform/primitives/Panel'
import { Money } from '../../platform/primitives/Money'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { emptyUnlessGroups, healthFor, isLive, linkedGroupsOf, type InstitutionGroup } from './connectionGroups'
import { accountGroupsLabel } from './accountGroups'
import { connectionsStaleLabel } from './accountsStaleLabels'
import { balancesForTotals, countsTowardTotals, totalsOf, type Totals } from './accountMoney'
import { useProviderItems } from './useAggregator'
import type { Account } from './useAccounts'
import './accounts.css'

/**
 * The four summary tiles — M5 Ticket 1 (#425 §A1/§A2). Replaces the old
 * three-tile row (Total assets / Total liabilities / Net worth, the last
 * OMITTED on mixed currency) with the current design: **Net worth (hero) ·
 * Assets · Liabilities · Connections**, always four, in this order, never
 * reflowing to three.
 *
 * Extracted from `Accounts.tsx` rather than left inline, matching the
 * plan's own file table. That move surfaced one thing the plan does not
 * mention: `PerCurrencySubtotals` was a non-exported local in `Accounts.tsx`
 * — moving the CALLERS here without moving the definition would have forced
 * a choice between duplicating it or reaching back into a route module from
 * a component it no longer owns. It comes with them.
 */

/**
 * Every currency present among accounts that have a balance and count toward totals — the order
 * `totalsOf` itself would have summed them in, before it refused to.
 *
 * Exported for `InstitutionsPanel.tsx` (Ticket 2) — a single institution can
 * mix currencies exactly the way the whole household can, and a row's total
 * owes it the identical discipline rather than a second implementation.
 */
export function currenciesOf(accounts: Account[]): string[] {
  return [...new Set(balancesForTotals(accounts).map((a) => a.currency))]
}

/**
 * The per-currency fallback for a mixed-currency household — one row per
 * currency, using the SAME field of `totalsOf`'s result for every tile that
 * calls this. Shared by all three money tiles (Net worth, Assets,
 * Liabilities) so the three answer the mixed-currency case identically
 * rather than inventing their own treatment per tile (architect review,
 * issue #395 non-blocking, carried forward from the three-tile row).
 *
 * **Net worth is now included** — the three-tile design omitted it outright
 * on mixed currency (M5-R8/D21's "no single figure would be honest"); the
 * four-tile design keeps the conclusion (no single number) but not the
 * omission (the tile itself must not disappear, or the grid goes 4 → 3 and
 * reflows). Per-currency net-worth lines are exactly as honest as the old
 * omission and keep the tile present.
 *
 * Exported for `InstitutionsPanel.tsx` (Ticket 2), the row total's own
 * mixed-currency fallback — never a second rendering of the identical
 * per-currency fanout.
 */
export function PerCurrencySubtotals({
  accounts,
  currencies,
  pick,
}: {
  accounts: Account[]
  currencies: string[]
  pick: (totals: Totals) => number
}) {
  return (
    <span className="accounts-total__per-currency">
      {currencies.map((currency) => {
        const t = totalsOf(accounts.filter((a) => a.currency === currency))
        return (
          <span key={currency} className="accounts-total__per-currency-row">
            <Money cents={t ? pick(t) : 0} currency={currency} kind="stock" />
          </span>
        )
      })}
    </span>
  )
}

const CONNECTIONS_SKELETON = (
  <>
    <Skeleton height={20} width={60} />
    <Skeleton height={13} width={110} />
  </>
)

/**
 * The tile's health line — `accountHealth()` returns FOUR kinds (`ok`,
 * `manual`, `stale`, `reauth`), and this used to count only `reauth`
 * (architect review, #425, blocking): a household with a merely-stale
 * connection was told "All connected", a false assurance sitting on the
 * same page as `Connections.tsx`'s own stale note and right before Ticket
 * 3's "Stale" pill on the card.
 *
 * **Reauth and stale get their OWN words, never folded into one sentence**
 * — they are different actions (reconnect vs. nothing to do but wait) — and
 * **reauth is checked first, matching `accountHealth`'s own precedence**:
 * it is the state that blocks syncing, so it leads. `disconnected`
 * is reported last and apart — it is not live and not a fault.
 */
function summarizeConnectionHealth(groups: InstitutionGroup[]): string {
  let reauth = 0
  let stale = 0
  let disconnected = 0
  // `healthFor` — the same derivation the Institutions panel's rows use, over
  // the same joined groups, so a disconnected group (which may have no item, and so is
  // invisible to a loop over `items`) counts here exactly as it reads there.
  for (const group of groups) {
    const health = healthFor(group)
    if (health?.kind === 'reauth') reauth += 1
    else if (health?.kind === 'disconnected') disconnected += 1
    else if (health?.kind === 'stale') stale += 1
  }
  if (reauth === 0 && stale === 0 && disconnected === 0) return 'All connected'

  const parts: string[] = []
  if (reauth > 0) parts.push(`${reauth} need${reauth === 1 ? 's' : ''} reconnecting`)
  if (stale > 0) parts.push(`${stale} out of date`)
  // Not a fault: a disconnected bank is one the user can connect again later,
  // so it is reported apart from the live health words above.
  if (disconnected > 0) parts.push(`${disconnected} disconnected`)
  return parts.join(', ')
}

/**
 * The Connections tile — reads `useProviderItems()` through its OWN nested
 * `<Async>`, a second surface alongside the page's own `surface="accounts"`
 * (the `OwnerMenu` precedent, `AccountDetail.tsx`'s `OwnerMenu`). Health
 * comes from `connectionGroups.ts#healthFor` over `linkedGroupsOf`'s joined groups (never a loop over `items`, which cannot see an orphan), and emptiness from `emptyUnlessGroups`.
 *
 * `staleLabel` names its OWN subject — "Connection status from …" — rather
 * than `Async`'s default "Showing data from …", which the page's own
 * `surface="accounts"` banner already renders; for a single-institution
 * household the two surfaces' `asOf` trace to the same `last_synced_at`, so
 * without this they said the byte-identical sentence twice on one page
 * (architect review, #425, blocking — `account-detail.spec.ts:377`).
 */
function ConnectionsTile({ accounts }: { accounts: Account[] }) {
  // Emptiness judged on the joined groups, as the panel does — see
  // `emptyUnlessGroups`.
  const state = emptyUnlessGroups(useProviderItems(), accounts)
  const navigate = useNavigate()

  return (
    <Panel role="group" aria-label="Connections">
      <span className="accounts-total__label">Connections</span>
      <Async
        surface="aggregator-items"
        state={state}
        skeleton={CONNECTIONS_SKELETON}
        empty={{
          title: 'No connections yet',
          action: { label: 'Link a bank', onAct: () => navigate('/accounts/connect') },
        }}
        staleLabel={connectionsStaleLabel}
      >
        {(items) => {
          // The SAME joined groups `InstitutionsPanel` renders: a count and a
          // status read off `items` alone cannot see an orphan (#501).
          const groups = linkedGroupsOf(accounts, items)
          // Live connections only — a disconnected institution is
          // reported in the sub-line, not counted here.
          const liveCount = groups.filter(isLive).length
          return (
            <>
              <span className="accounts-summary__count">
                {`${liveCount} ${liveCount === 1 ? 'connection' : 'connections'}`}
              </span>
              {/* Text, not colour alone (rule 11) — the count above already
                  says HOW MANY; this says whether any of them need action. */}
              <span className="accounts-summary__sub">{summarizeConnectionHealth(groups)}</span>
            </>
          )
        }}
      </Async>
    </Panel>
  )
}

export function AccountsSummary({ accounts }: { accounts: Account[] }) {
  const totals = totalsOf(accounts)
  const currencies = currenciesOf(accounts)
  // Sub-lines are DERIVED from the groups actually present — never a
  // hard-coded "Cash + investments", which is wrong the moment a household
  // has no investments (or, going forward, ever gets a second liability
  // group beyond `credit`).
  const counted = balancesForTotals(accounts)
  const assetGroups = accountGroupsLabel(counted, false)
  const liabilityGroups = accountGroupsLabel(counted, true)
  // Disconnected accounts that carry a balance — money the three tiles leave out.
  const excluded = accounts.filter((a) => a.currentCents !== null && !countsTowardTotals(a)).length
  // Issue 412's own fix, carried over from the three-tile row: below 640px
  // the grid's own `minmax(212px, 1fr)` floor cannot be trusted to land on
  // the SAME breakpoint every other stacked layout in this app uses, so the
  // explicit modifier still comes from `useBreakpoint()` — the one legal
  // source (rule 5), never a media query in `accounts.css`.
  const phone = useBreakpoint() === 'phone'

  return (
    <div className={`accounts-summary${phone ? ' accounts-summary--phone' : ''}`}>
      {/* Rule 9: the lime fill goes on the Net worth tile — the hero moved
          here from Total assets (plan's Global Constraints). "＋ Connect
          account" in the toolbar above is a non-accent (`outline`) primary,
          so this content area still spends its one `--accent` role once. */}
      <HeroTile role="group" aria-label="Net worth">
        <span className="accounts-total__label">Net worth</span>
        {totals ? (
          <Money
            cents={totals.netWorthCents}
            currency={totals.currency ?? undefined}
            variant="hero"
            kind="stock"
          />
        ) : (
          <PerCurrencySubtotals
            accounts={accounts}
            currencies={currencies}
            pick={(t) => t.netWorthCents}
          />
        )}
      </HeroTile>

      <Panel role="group" aria-label="Assets">
        <span className="accounts-total__label">Assets</span>
        {totals ? (
          <Money cents={totals.assetsCents} currency={totals.currency ?? undefined} kind="stock" />
        ) : (
          <PerCurrencySubtotals accounts={accounts} currencies={currencies} pick={(t) => t.assetsCents} />
        )}
        {assetGroups && <span className="accounts-summary__sub">{assetGroups}</span>}
      </Panel>

      <Panel role="group" aria-label="Liabilities">
        <span className="accounts-total__label">Liabilities</span>
        <span className="accounts-summary__figure--neg">
          {totals ? (
            <Money cents={totals.liabilitiesCents} currency={totals.currency ?? undefined} kind="stock" />
          ) : (
            <PerCurrencySubtotals
              accounts={accounts}
              currencies={currencies}
              pick={(t) => t.liabilitiesCents}
            />
          )}
        </span>
        {liabilityGroups && <span className="accounts-summary__sub">{liabilityGroups}</span>}
      </Panel>

      <ConnectionsTile accounts={accounts} />

      {/* Q4, said in words: a figure that silently omits money is a lie by
          omission. Absent when nothing was left out. */}
      {excluded > 0 && (
        <p className="note accounts-summary__excluded">
          {`Not included: ${excluded} disconnected ${excluded === 1 ? 'account' : 'accounts'}`}
        </p>
      )}
    </div>
  )
}
